import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { ThemeEvents } from '@theme/events';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogComponent } from '@theme/dialog';

/**
 * 仅以下关键词使用精确搜索。
 * 关键词统一使用小写；后续有其他异常关键词时，可继续添加。
 */
const strictSearchTerms = new Set(['acceptance']);

/**
 * 清除搜索词最外层的双引号。
 * @param {string | null | undefined} term
 * @returns {string}
 */
const cleanSearchTerm = (term) => {
  let value = String(term || '').trim();

  if (
    value.length >= 2 &&
    value.startsWith('"') &&
    value.endsWith('"')
  ) {
    value = value.slice(1, -1).trim();
  }

  return value;
};

/**
 * ACCEPTANCE 转为 "ACCEPTANCE"；
 * og1 等其他关键词保持普通搜索，不影响 Shopify 产品提升。
 * @param {string | null | undefined} term
 * @returns {string}
 */
const buildSearchTerm = (term) => {
  const value = cleanSearchTerm(term);

  if (!value) return '';

  if (strictSearchTerms.has(value.toLowerCase())) {
    return `"${value.replace(/"/g, '')}"`;
  }

  return value;
};

/**
 * 创建或更新搜索表单中的隐藏参数。
 * @param {HTMLFormElement} form
 * @param {string} name
 * @param {string} value
 */
const setHiddenInput = (form, name, value) => {
  let input = form.querySelector(`input[name="${name}"]`);

  if (!(input instanceof HTMLInputElement)) {
    input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    form.appendChild(input);
  }

  input.value = value;
};

/**
 * 判断当前地址是否为正式搜索结果页。
 * @param {string} pathname
 * @returns {boolean}
 */
const isSearchPath = (pathname) => {
  const searchPath = new URL(
    Theme.routes.search_url,
    location.origin
  ).pathname.replace(/\/$/, '');

  return pathname.replace(/\/$/, '') === searchPath;
};

/**
 * 统一旧搜索链接：
 * 1. ACCEPTANCE 自动转换为 "ACCEPTANCE"；
 * 2. "og1" 等非严格关键词自动恢复为 og1；
 * 3. 正式搜索页固定为商品搜索和 prefix=none。
 * @returns {boolean} 是否已经触发重定向
 */
const normalizeCurrentSearchUrl = () => {
  const url = new URL(window.location.href);

  if (!isSearchPath(url.pathname)) return false;

  const currentTerm = url.searchParams.get('q');

  if (!currentTerm) return false;

  const normalizedTerm = buildSearchTerm(currentTerm);
  let shouldRedirect = false;

  if (currentTerm !== normalizedTerm) {
    url.searchParams.set('q', normalizedTerm);
    shouldRedirect = true;
  }

  if (url.searchParams.get('type') !== 'product') {
    url.searchParams.set('type', 'product');
    shouldRedirect = true;
  }

  if (
    url.searchParams.get('options[prefix]') !== 'none'
  ) {
    url.searchParams.set(
      'options[prefix]',
      'none'
    );
    shouldRedirect = true;
  }

  if (shouldRedirect) {
    window.location.replace(url.toString());
    return true;
  }

  return false;
};

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = [
    'searchInput',
    'predictiveSearchResults',
    'resetButton',
  ];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #resizeObserver = new ResizeObserver((entries) => {
    for (const entry of entries) {
      this.style.setProperty(
        '--predictive-search-results-height',
        `${entry.contentRect.height}px`
      );
    }
  });

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    /*
     * 地址栏中可以保留精确搜索的双引号，
     * 但搜索框只显示正常关键词。
     */
    this.refs.searchInput.value = cleanSearchTerm(
      this.refs.searchInput.value
    );

    const searchForm =
      this.refs.searchInput.form ??
      this.closest('form') ??
      this.querySelector('form');

    if (searchForm instanceof HTMLFormElement) {
      searchForm.addEventListener(
        'submit',
        this.#handleSearchSubmit,
        { signal }
      );
    }

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener(
        'keydown',
        this.#handleKeyboardShortcut,
        { signal }
      );

      dialog.addEventListener(
        DialogCloseEvent.eventName,
        this.#handleDialogClose,
        { signal }
      );

      this.addEventListener(
        'click',
        this.#handleModalClick,
        { signal }
      );
    } else {
      document.addEventListener(
        ThemeEvents.megaMenuHover,
        this.#blurSearch,
        { signal }
      );
    }

    this.#getRecentlyViewed();

    const results =
      this.refs.predictiveSearchResults.firstElementChild;

    if (results) {
      this.#resizeObserver.observe(results);
    }
  }

  /**
   * 提交正式搜索表单时统一搜索词及搜索参数。
   * @param {SubmitEvent} event
   */
  #handleSearchSubmit = (event) => {
    const form = event.currentTarget;

    if (!(form instanceof HTMLFormElement)) return;

    const searchInput = form.querySelector(
      'input[name="q"]'
    );

    if (!(searchInput instanceof HTMLInputElement)) {
      return;
    }

    const searchTerm = buildSearchTerm(
      searchInput.value
    );

    if (!searchTerm) return;

    searchInput.value = searchTerm;

    setHiddenInput(
      form,
      'type',
      'product'
    );

    setHiddenInput(
      form,
      'options[prefix]',
      'none'
    );
  };

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (
      event.target
    );

    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (
      !isInteractiveElement &&
      this.refs.searchInput
    ) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    this.#resizeObserver.disconnect();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (
      event.metaKey &&
      event.key === 'k'
    ) {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  expandSearch = () => {
    this.classList.add(
      'predictive-search--expanded'
    );

    if (this.dataset.activeColorScheme) {
      const target = this.dialog ?? this;

      target.classList.add(
        `color-${this.dataset.activeColorScheme}`
      );
    }
  };

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (
          container.classList.contains(
            'predictive-search-results__wrapper-products'
          )
        ) {
          return Array.from(
            container.querySelectorAll(
              '.predictive-search-results__card'
            )
          );
        }

        return Array.from(
          container.querySelectorAll(
            '[ref="resultsItems[]"], ' +
              '.predictive-search-results__card'
          )
        );
      })
      .filter(
        (item) => item instanceof HTMLElement
      );

    return /** @type {HTMLElement[]} */ (
      allItems
    );
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return (
      this.#allResultsItems?.findIndex(
        (item) =>
          item.getAttribute(
            'aria-selected'
          ) === 'true'
      ) ?? -1
    );
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [
      itemIndex,
      item,
    ] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute(
          'aria-selected',
          'true'
        );

        if (this.#isKeyboardNavigation) {
          item.classList.add(
            'keyboard-focus'
          );
        }

        item.scrollIntoView({
          behavior: prefersReducedMotion()
            ? 'instant'
            : 'smooth',
          block: 'nearest',
        });
      } else {
        item.removeAttribute(
          'aria-selected'
        );
      }
    }

    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[
      this.#currentIndex
    ];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (
      !this.#allResultsItems?.length ||
      event.key === 'ArrowLeft' ||
      event.key === 'ArrowRight'
    ) {
      return;
    }

    const currentIndex =
      this.#currentIndex;

    const totalItems =
      this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();

        this.#currentIndex =
          currentIndex < totalItems - 1
            ? currentIndex + 1
            : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();

          this.#currentIndex =
            currentIndex > 0
              ? currentIndex - 1
              : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();

          this.#currentIndex =
            currentIndex < totalItems - 1
              ? currentIndex + 1
              : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();

        this.#currentIndex =
          currentIndex > 0
            ? currentIndex - 1
            : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer =
          this.refs.predictiveSearchResults.querySelector(
            '[data-single-result-url]'
          );

        if (
          singleResultContainer instanceof
            HTMLElement &&
          singleResultContainer.dataset
            .singleResultUrl
        ) {
          event.preventDefault();

          window.location.href =
            singleResultContainer.dataset.singleResultUrl;

          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();

          this.#currentItem
            ?.querySelector('a')
            ?.click();
        } else {
          event.preventDefault();

          const searchUrl = new URL(
            Theme.routes.search_url,
            location.origin
          );

          searchUrl.searchParams.set(
            'q',
            buildSearchTerm(
              this.refs.searchInput.value
            )
          );

          searchUrl.searchParams.set(
            'type',
            'product'
          );

          searchUrl.searchParams.set(
            'options[prefix]',
            'none'
          );

          window.location.href =
            searchUrl.toString();
        }

        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const {
      recentlyViewedItems,
      recentlyViewedTitle,
      recentlyViewedWrapper,
    } = this.refs;

    const allRecentlyViewedElements = [
      ...(recentlyViewedItems || []),
      ...(recentlyViewedTitle || []),
    ];

    if (
      allRecentlyViewedElements.length === 0
    ) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add(
        'removing'
      );

      onAnimationEnd(
        recentlyViewedWrapper,
        () => {
          recentlyViewedWrapper.remove();
        }
      );
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce(
    (keepFocus = true) => {
      if (keepFocus) {
        this.refs.searchInput.focus();
      }

      this.#resetSearch();
    },
    100
  );

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    /*
     * If the input is not a text input
     * (like using the Escape key),
     * don't search.
     */
    if (!event.inputType) return;

    const searchTerm =
      this.refs.searchInput.value.trim();

    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      const resultsInner =
        this.refs.predictiveSearchResults.querySelector(
          '.predictive-search-results__inner'
        );

      if (
        resultsInner instanceof HTMLElement
      ) {
        resultsInner.scrollTop = 0;
      }

      const formContent =
        this.querySelector(
          '.predictive-search-form__content'
        );

      if (
        formContent instanceof HTMLElement
      ) {
        formContent.scrollTop = 0;
      }
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(
      Theme.routes.predictive_search_url,
      location.origin
    );

    /*
     * 预测搜索和正式搜索使用相同的关键词规则。
     * ACCEPTANCE 使用精确搜索；
     * og1 等普通关键词不加引号。
     */
    url.searchParams.set(
      'q',
      buildSearchTerm(searchTerm)
    );

    url.searchParams.set(
      'resources[limit_scope]',
      'each'
    );

    const {
      predictiveSearchResults,
    } = this.refs;

    const abortController =
      this.#createAbortController();

    sectionRenderer
      .getSectionHTML(
        this.dataset.sectionId,
        false,
        url
      )
      .then((resultsMarkup) => {
        if (!resultsMarkup) return;

        if (
          abortController.signal.aborted
        ) {
          return;
        }

        morph(
          predictiveSearchResults,
          resultsMarkup
        );

        this.#resetScrollPositions();
      })
      .catch((error) => {
        if (
          abortController.signal.aborted
        ) {
          return;
        }

        throw error;
      });
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>}
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) {
      return null;
    }

    const viewedProducts =
      RecentlyViewed.getProducts();

    if (viewedProducts.length === 0) {
      return null;
    }

    const url = new URL(
      Theme.routes.search_url,
      location.origin
    );

    url.searchParams.set(
      'q',
      viewedProducts
        .map(
          /** @param {string} id */
          (id) => `id:${id}`
        )
        .join(' OR ')
    );

    url.searchParams.set(
      'resources[type]',
      'product'
    );

    return sectionRenderer.getSectionHTML(
      this.dataset.sectionId,
      false,
      url
    );
  }

  /**
   * Fetch recently viewed products using the section renderer and update the results container.
   */
  async #getRecentlyViewed() {
    const {
      predictiveSearchResults,
    } = this.refs;

    const abortController =
      this.#createAbortController();

    try {
      const resultsMarkup =
        await this.#getRecentlyViewedProductsMarkup();

      if (!resultsMarkup) return;

      const parsedNextPage =
        new DOMParser().parseFromString(
          resultsMarkup,
          'text/html'
        );

      const recentlyViewedProductsHtml =
        parsedNextPage.getElementById(
          'predictive-search-products'
        );

      if (!recentlyViewedProductsHtml) {
        return;
      }

      for (
        const child of
        recentlyViewedProductsHtml.children
      ) {
        if (child instanceof HTMLElement) {
          child.setAttribute(
            'ref',
            'recentlyViewedWrapper'
          );
        }
      }

      const collectionElement =
        predictiveSearchResults.querySelector(
          '#predictive-search-products'
        );

      if (!collectionElement) return;

      if (
        this.refs.recentlyViewedWrapper
      ) {
        this.refs.recentlyViewedWrapper.remove();
      }

      if (
        abortController.signal.aborted
      ) {
        return;
      }

      collectionElement.prepend(
        ...recentlyViewedProductsHtml.children
      );
    } catch (error) {
      throw error;
    }
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController =
      new AbortController();

    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    this.#activeFetch =
      abortController;

    return abortController;
  }

  #resetSearch = async () => {
    const {
      predictiveSearchResults,
      searchInput,
    } = this.refs;

    const emptySectionId =
      'predictive-search-empty';

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();

    const abortController =
      this.#createAbortController();

    const emptySectionMarkup =
      await sectionRenderer.getSectionHTML(
        emptySectionId,
        false
      );

    const parsedEmptySectionMarkup =
      new DOMParser()
        .parseFromString(
          emptySectionMarkup,
          'text/html'
        )
        .querySelector(
          '.predictive-search-empty-section'
        );

    if (!parsedEmptySectionMarkup) {
      throw new Error(
        'No empty section markup found'
      );
    }

    /**
     * This needs to be awaited and not .then
     * so the DOM is already morphed when
     * #closeResults is called and therefore
     * the height is animated.
     */
    const viewedProducts =
      RecentlyViewed.getProducts();

    if (viewedProducts.length > 0) {
      const recentlyViewedMarkup =
        await this.#getRecentlyViewedProductsMarkup();

      if (!recentlyViewedMarkup) return;

      const parsedRecentlyViewedMarkup =
        new DOMParser().parseFromString(
          recentlyViewedMarkup,
          'text/html'
        );

      const recentlyViewedProductsHtml =
        parsedRecentlyViewedMarkup.getElementById(
          'predictive-search-products'
        );

      if (!recentlyViewedProductsHtml) {
        return;
      }

      for (
        const child of
        recentlyViewedProductsHtml.children
      ) {
        if (child instanceof HTMLElement) {
          child.setAttribute(
            'ref',
            'recentlyViewedWrapper'
          );
        }
      }

      const collectionElement =
        parsedEmptySectionMarkup.querySelector(
          '#predictive-search-products'
        );

      if (!collectionElement) return;

      collectionElement.prepend(
        ...recentlyViewedProductsHtml.children
      );
    }

    if (
      abortController.signal.aborted
    ) {
      return;
    }

    morph(
      predictiveSearchResults,
      parsedEmptySectionMarkup
    );

    this.#resetScrollPositions();
  };

  /**
   * Closes the predictive search.
   */
  #blurSearch = () => {
    this.refs.searchInput.blur();
  };
}

/*
 * 页面打开时统一处理旧搜索地址。
 */
normalizeCurrentSearchUrl();

if (
  !customElements.get(
    'predictive-search-component'
  )
) {
  customElements.define(
    'predictive-search-component',
    PredictiveSearchComponent
  );
}